import {
  PeopleTableLive,
  chatArtifactPanelStore,
} from "@/stores/chat-artifact-panel-store";
import { SearchPeopleToolRunning } from "@shaarp/common";
import { useEffect, useMemo, useRef } from "react";

/**
 * Keeps the side panel on a run watched live: opened on its first progress,
 * fed with each one. Once the run ends, whatever it ended on, the panel stops
 * waiting: a table that holds people switches to its saved rows, one that
 * holds nobody — or a search that failed — gives the panel back.
 */
export function useLivePeopleTablePanel({
  running,
  isLive,
  canShowTable,
  isSettled,
  hasPeople,
}: {
  running: SearchPeopleToolRunning | null;
  isLive: boolean;
  canShowTable: boolean;
  isSettled: boolean;
  hasPeople: boolean;
}): void {
  const live = useMemo<PeopleTableLive | null>(
    () =>
      running && isLive
        ? {
            query: running.query,
            peopleCount: running.peopleCount,
            rows: running.liveRows ?? [],
            status: running.liveStatus ?? null,
          }
        : null,
    [running, isLive],
  );

  const liveSearchIdRef = useRef<string | null>(null);
  const settledSearchIdRef = useRef<string | null>(null);
  const runningSearchId = running?.searchId ?? null;

  useEffect(() => {
    if (!canShowTable || !live || !runningSearchId) return;
    if (liveSearchIdRef.current === runningSearchId) {
      chatArtifactPanelStore.updatePeopleTableLive(runningSearchId, live);
      return;
    }
    liveSearchIdRef.current = runningSearchId;
    chatArtifactPanelStore.openPeopleTable(runningSearchId, live);
  }, [canShowTable, live, runningSearchId]);

  useEffect(() => {
    const liveSearchId = liveSearchIdRef.current;
    if (!isSettled || !liveSearchId) return;
    if (settledSearchIdRef.current === liveSearchId) return;
    settledSearchIdRef.current = liveSearchId;
    if (hasPeople) {
      chatArtifactPanelStore.updatePeopleTableLive(liveSearchId, null);
    } else {
      chatArtifactPanelStore.closePeopleTable(liveSearchId);
    }
  }, [isSettled, hasPeople]);
}
